import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import './Astra.css';
import { FormatText } from '../../utils/textFormatter';
import Loader from '../Loader/Loader';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import PageHeader from '../Page/PageHeader';
import { Helmet } from 'react-helmet';

const Astra = () => {
    const [formData, setFormData] = useState({
        birth_date: '',
        birth_time: '',
        city: '',
        latitude: '',
        longitude: '',
        utc_offset: ''
    });

    const [citySearch, setCitySearch] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [rawData, setRawData] = useState(null);
    const [fullData, setFullData] = useState(null);
    const [loadingState, setLoadingState] = useState('idle'); // 'idle', 'raw', 'full'
    const [error, setError] = useState(null);
    const [trial, setTrial] = useState(() => {
        // Si le localStorage n'existe pas encore, on initialise à true
        const storedTrial = localStorage.getItem('astra_trial');
        return storedTrial === null ? true : storedTrial === 'true';
    });
    const { guid } = useParams();

    const suggestionTimeoutRef = useRef(null);

    // Configuration des remplacements
    const textReplacements = [
        {
            words: ['Bélier', 'Taureau', 'Gémeaux', 'Cancer', 'Lion', 'Vierge', 'Balance',
                'Scorpion', 'Sagittaire', 'Capricorne', 'Verseau', 'Poissons'],
            className: 'sign-text',
            symbol: {
                'Bélier': 'a',
                'Taureau': 'b',
                'Gémeaux': 'c',
                'Cancer': 'd',
                'Lion': 'e',
                'Vierge': 'f',
                'Balance': 'g',
                'Scorpion': 'h',
                'Sagittaire': 'i',
                'Capricorne': 'j',
                'Verseau': 'k',
                'Poissons': 'l'
            }
        },
        {
            words: [
                'Soleil', 'Lune', 'Mercure', 'Vénus', 'Mars', 'Jupiter', 'Saturne',
                'Uranus', 'Neptune', 'Pluton', 'Chiron', 'Cérès', 'Pallas', 'Junon',
                'Vesta', 'Lilith (moyenne)', 'Lilith (vraie)', 'Nœud Nord', 'Nœud Sud',
                'Part de Fortune'
            ],
            className: 'planet-text',
            symbol: {
                'Soleil': 'A',
                'Lune': 'B',
                'Mercure': 'C',
                'Vénus': 'D',
                'Mars': 'E',
                'Jupiter': 'F',
                'Saturne': 'G',
                'Uranus': 'H',
                'Neptune': 'I',
                'Pluton': 'K'
            }
        },
        {
            words: ['[Mm]aison\\s*\\d+|(?<=en maison\\s*)\\d+'],
            className: 'house-text',
            useRegex: true,
            getClass: (match) => `maison${match.match(/\d+/)[0]}-text`
        }
    ];

    // Textes pour le SEO
    const seoContent = {
        faq: [
            {
                question: "Comment connaître son thème astral ?",
                answer: "Entrez votre date, votre heure et votre lieu de naissance dans le formulaire ci-dessus. En un clic, AstraLight calcule les positions planétaires exactes et génère votre thème astral personnalisé."
            },
            {
                question: "Comment puis-je comprendre mon thème astral ?",
                answer: "Chaque planète, signe et maison est expliqué dans vos résultats avec des interprétations détaillées. Consultez aussi la section 'Interpréter vos résultats' plus bas pour un guide complet."
            },
            {
                question: "Pourquoi faire son thème astral ?",
                answer: "Votre thème astral est une carte d'identité cosmique unique qui révèle vos forces, vos défis et votre potentiel. C'est la base pour toute exploration astrologique sérieuse et la compréhension de votre personnalité profonde."
            },
            {
                question: "Quel ascendant suis-je ?",
                answer: "Renseignez votre heure précise de naissance dans le formulaire : l'algorithme calcule automatiquement votre Ascendant et l'affiche dans les résultats avec son interprétation."
            },
            {
                question: "Qu'est-ce qu'un ascendant ?",
                answer: "L'Ascendant est le signe zodiacal qui se levait à l'horizon Est au moment de votre naissance. Il influence votre apparence, votre comportement extérieur et la façon dont vous abordez le monde."
            },
            {
                question: "Comment faire si je ne connais pas mon heure de naissance ?",
                answer: "Sans heure précise, le calcul sera moins exact pour l'ascendant et les maisons. Vous pouvez indiquer midi (12:00) pour obtenir une position planétaire correcte mais avec un ascendant approximatif."
            },
            {
                question: "Est-ce que le thème astral est payant ?",
                answer: "Non, le calcul et un aperçu interprété sont 100% gratuits. L'analyse détaillée complète est offerte en échange de votre email (toujours gratuit, pas de carte bancaire requise)."
            },
            {
                question: "Comment calculer sa maison astrale ?",
                answer: "AstraLight positionne automatiquement vos planètes dans les 12 maisons grâce à vos coordonnées géographiques et votre fuseau horaire. Aucun calcul manuel n'est nécessaire."
            },
            {
                question: "Quels sont les signes dominants du thème astral ?",
                answer: "Les signes où vous avez le plus de planètes (ou des points clés comme l'Ascendant) sont considérés comme dominants. Après le calcul, observez la répartition de vos planètes dans les différents signes."
            }
        ]
    };

    // Composant pour l'article informatif sur le thème astral
    const ThemeAstralInfoContent = () => (
        <div className="article panel">
            <h2>Qu'est-ce qu'un thème astral ?</h2>
            <p>
                Le thème astral, également appelé carte du ciel ou thème natal, est une représentation symbolique du ciel au moment précis de votre naissance. Cette empreinte céleste unique capture les positions exactes des planètes, des astéroïdes et des points calculés par rapport aux signes du zodiaque et aux maisons astrologiques.
            </p>
            <p>
                Véritable carte d'identité cosmique, le thème astral va bien au-delà du simple signe solaire que vous connaissez probablement. Il révèle la complexité de votre personnalité, vos talents innés, vos défis potentiels et les dynamiques énergétiques qui vous accompagnent tout au long de votre vie.
            </p>
            <p>
                Chaque élément du thème - planètes, signes, maisons et aspects - représente une facette différente de votre être. Leur interprétation combinée offre une compréhension profonde de votre individualité et de votre chemin de vie.
            </p>

            <h2>Comment se calcule votre carte du ciel ?</h2>
            <p>
                Le calcul précis d'un thème astral nécessite trois données essentielles : votre date de naissance complète (jour, mois, année), l'heure exacte de naissance, et le lieu précis (coordonnées géographiques de latitude et longitude).
            </p>
            <p>
                Ces informations permettent de déterminer avec précision les positions des corps célestes au moment où vous avez pris votre première respiration. Notre outil utilise les éphémérides astronomiques officielles et les algorithmes de calcul les plus précis pour positionner chaque planète dans le signe et la maison correspondante.
            </p>
            <p>
                L'ascendant, point fondamental du thème qui détermine l'orientation des maisons astrologiques, est particulièrement sensible à l'heure de naissance. C'est pourquoi une heure précise est cruciale pour un calcul fiable. Notre système prend également en compte le fuseau horaire et les ajustements historiques (comme l'heure d'été) pour garantir l'exactitude de votre thème.
            </p>
        </div>
    );

    // Composant pour l'interprétation des résultats
    const InterpretationGuide = () => (
        <div className="interpretation panel">
            <h2>Interpréter vos résultats</h2>
            
            <h3>Les planètes et leurs influences</h3>
            <p>
                Chaque planète représente une fonction psychologique ou un domaine d'expression spécifique. Le Soleil révèle votre essence et votre vitalité, la Lune vos émotions et instincts, Mercure votre façon de communiquer, Vénus votre rapport à l'amour et aux valeurs, Mars votre énergie et votre assertion.
            </p>
            
            <h3>L'importance des signes</h3>
            <p>
                Le signe zodiacal dans lequel se trouve une planète colore son expression. Un Soleil en Bélier s'exprimera avec dynamisme et spontanéité, tandis qu'un Soleil en Taureau cherchera stabilité et confort. Chaque combinaison planète-signe offre une lecture nuancée de votre personnalité.
            </p>
            
            <h3>Les maisons astrologiques</h3>
            <p>
                Les douze maisons représentent les différents domaines de votre vie. La maison où se trouve une planète indique dans quel secteur son énergie se manifestera principalement. Une Vénus en maison 7 parlera d'harmonie dans les relations, tandis qu'en maison 10, elle pourra signifier une carrière liée à l'art ou la beauté.
            </p>
            
            <h3>Les aspects entre planètes</h3>
            <p>
                Les aspects sont les angles formés entre les planètes. Ils révèlent comment leurs énergies interagissent - en harmonie (trigones, sextiles) ou en tension (carrés, oppositions). Ces dynamiques influencent profondément votre expérience de vie et vos schémas comportementaux.
            </p>
        </div>
    );

    // Composant pour la FAQ
    const FAQ = () => (
        <div className="faq panel">
            <h2>Questions fréquentes sur le thème astral</h2>
            
            <div className="faq-items" itemScope itemType="https://schema.org/FAQPage">
                {seoContent.faq.map((item, index) => (
                    <div key={index} className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                        <details>
                            <summary itemProp="name">{item.question}</summary>
                            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                <p itemProp="text">{item.answer}</p>
                            </div>
                        </details>
                    </div>
                ))}
            </div>
            
            <div className="additional-resources" style={{ marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #eaeaea" }}>
                <h3>Ressources supplémentaires</h3>
                <p>Vous souhaitez explorer davantage les mystères de l'astrologie ? Découvrez comment votre thème interagit avec celui d'une autre personne via notre <Link to="/compatibilite-amoureuse-astrologie">outil de compatibilité astrologique</Link>.</p>
            </div>
        </div>
    );

    // Schema.org pour l'outil de calcul
    const getToolSchema = () => {
        return {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Calcul de Thème Astral",
            "applicationCategory": "Astrologie",
            "operatingSystem": "Web",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "EUR"
            },
            "description": "Calculez gratuitement votre thème astral avec des positions planétaires précises et une interprétation personnalisée."
        };
    };

    // Schema.org pour HowTo - comment calculer son thème astral
    const getHowToSchema = () => {
        return {
            "@context": "https://schema.org",
            "@type": "HowTo", 
            "name": "Comment calculer son thème astral",
            "description": "Guide pas à pas pour calculer votre thème astral personnel avec précision",
            "step": [
                {
                    "@type": "HowToStep",
                    "name": "Entrez votre date de naissance",
                    "text": "Saisissez votre date complète de naissance (jour, mois, année)"
                },
                {
                    "@type": "HowToStep",
                    "name": "Précisez votre heure de naissance",
                    "text": "Pour des résultats précis, indiquez l'heure exacte de votre naissance"
                },
                {
                    "@type": "HowToStep",
                    "name": "Renseignez votre lieu de naissance",
                    "text": "Entrez votre ville de naissance pour calculer les coordonnées géographiques"
                }
            ],
            "tool": {
                "@type": "HowToTool",
                "name": "Calculateur de thème astral en ligne"
            }
        };
    };

    // Fonction pour rechercher les villes avec Nominatim
    const searchCities = async (query) => {
        console.log('🔍 Recherche de villes pour:', query);
        if (query.length < 3) {
            console.log('❌ Requête trop courte, minimum 3 caractères');
            setSuggestions([]);
            return;
        }

        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&featuretype=city`;
            console.log('📡 Appel API Nominatim:', url);

            const response = await fetch(url);
            const data = await response.json();
            console.log('📥 Données reçues de Nominatim:', data);

            const formattedSuggestions = data.map(item => ({
                display: `${item.name}, ${item.display_name.split(', ').slice(1).join(', ')}`,
                lat: item.lat,
                lon: item.lon,
                name: item.name
            }));

            console.log('✨ Suggestions formatées:', formattedSuggestions);
            setSuggestions(formattedSuggestions);
            setShowSuggestions(true);
        } catch (error) {
            console.error('❌ Erreur lors de la recherche des villes:', error);
        }
    };

    const handleCitySearch = (e) => {
        const value = e.target.value;
        setCitySearch(value);

        // Debounce la recherche
        if (suggestionTimeoutRef.current) {
            clearTimeout(suggestionTimeoutRef.current);
        }

        suggestionTimeoutRef.current = setTimeout(() => {
            searchCities(value);
        }, 300);
    };

    const handleCitySelect = async (suggestion) => {
        console.log('🎯 Ville sélectionnée:', suggestion);
        setShowSuggestions(false);
        setCitySearch(suggestion.display);

        const newFormData = {
            ...formData,
            city: suggestion.name,
            latitude: suggestion.lat,
            longitude: suggestion.lon
        };

        console.log('📝 Nouveau formData:', newFormData);
        setFormData(newFormData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            setLoadingState('raw');
            setRawData(null);
            setFullData(null);

            const rawResponse = await api.post('/get_raw_theme', formData);
            setRawData(rawResponse.data.data);

            setLoadingState('full');

            const fullResponse = await api.post('/calculate_theme', formData);
            setFullData(fullResponse.data.data);
            
            // Pour un nouveau thème, on s'assure que trial est à true
            if (localStorage.getItem('astra_trial') === null) {
                localStorage.setItem('astra_trial', 'true');
                setTrial(true);
            }

            setLoadingState('idle');
        } catch (err) {
            console.error('❌ Erreur:', err);
            setError(err.response?.data?.error || 'Une erreur est survenue');
            setLoadingState('idle');
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const renderJson = (data) => {
        if (typeof data === 'object' && data !== null) {
            return (
                <ul>
                    {Object.entries(data).map(([key, value]) => (
                        <li key={key}>
                            <strong>{key}:</strong> {renderJson(value)}
                        </li>
                    ))}
                </ul>
            );
        }
        return <span>{String(data)}</span>;
    };

    const DisplayAspects = ({ aspects }) => (
        <div className="section">
            <div className="section-header">
                <h2>Aspects Planétaires</h2>
                <hr />
                <p>Les aspects planétaires sont les angles formés entre les planètes dans un thème astral. Ils définissent la manière dont ces énergies interagissent, créant des harmonies, des tensions ou des défis à surmonter. Les principaux aspects incluent la conjonction (fusion des énergies), l'opposition (dualité et équilibre), le carré (tension et évolution), le trigone (fluidité et talent) et le sextile (opportunités et dynamisme). Leur interprétation affine la lecture du thème et met en lumière les dynamiques internes et relationnelles de la personne.</p>
            </div>
            <div className="aspects-section">
                <div className="panel">
                    <h3>Aspects Majeurs</h3>
                    {aspects.majeurs.map((aspect, index) => {
                        const hasValidRelations = aspect.relations.some(
                            relation => relation.interpretation && relation.interpretation.trim() !== ''
                        );

                        return hasValidRelations && (
                            <div key={index} className="aspect-card">
                                <div className="aspect-header">
                                    <h4><span>{aspect.nom}</span> ({aspect.angle}°)</h4>
                                    {aspect.description_courte && <p className="aspect-description">{aspect.description_courte}</p>}
                                    <hr />
                                </div>
                                {aspect.relations.map((relation, idx) => (
                                    relation.interpretation && relation.interpretation.trim() !== '' && (
                                        <div key={idx} className="relation">
                                            <h5>
                                                <span className="planet-text">{relation.planete1}<span className={`astro-symbol ${relation.planete1.toLowerCase()}-symbol`}>{textReplacements[1].symbol[relation.planete1]}</span></span>
                                                {' ('}{relation.degre1}°{relation.degre_relatif1 && `, ${relation.degre_relatif1}° ${relation.planete1_signe}`}) - {' '}
                                                <span className="planet-text">{relation.planete2}<span className={`astro-symbol ${relation.planete2.toLowerCase()}-symbol`}>{textReplacements[1].symbol[relation.planete2]}</span></span>
                                                {' ('}{relation.degre2}°{relation.degre_relatif2 && `, ${relation.degre_relatif2}° ${relation.planete2_signe}`})
                                            </h5>
                                            <p className="interpretation">{relation.interpretation}</p>
                                        </div>
                                    )
                                ))}
                            </div>
                        );
                    })}
                </div>
                <div className="panel">
                    <h3>Aspects Mineurs</h3>
                    {aspects.mineurs.map((aspect, index) => {
                        const hasValidRelations = aspect.relations.some(
                            relation => relation.interpretation && relation.interpretation.trim() !== ''
                        );

                        return hasValidRelations && (
                            <div key={index} className="aspect-card">
                                <div className="aspect-header">
                                    <h4><span>{aspect.nom}</span> ({aspect.angle}°)</h4>
                                    {aspect.description_courte && <p className="aspect-description">{aspect.description_courte}</p>}
                                    <hr />
                                </div>
                                {aspect.relations.map((relation, idx) => (
                                    relation.interpretation && relation.interpretation.trim() !== '' && (
                                        <div key={idx} className="relation">
                                            <h5>
                                                <span className="planet-text">{relation.planete1}<span className={`astro-symbol ${relation.planete1.toLowerCase()}-symbol`}>{textReplacements[1].symbol[relation.planete1]}</span></span>
                                                {' ('}{relation.degre1}°{relation.degre_relatif1 && `, ${relation.degre_relatif1}° ${relation.planete1_signe}`}) - {' '}
                                                <span className="planet-text">{relation.planete2}<span className={`astro-symbol ${relation.planete2.toLowerCase()}-symbol`}>{textReplacements[1].symbol[relation.planete2]}</span></span>
                                                {' ('}{relation.degre2}°{relation.degre_relatif2 && `, ${relation.degre_relatif2}° ${relation.planete2_signe}`})
                                            </h5>
                                            <p className="interpretation">{relation.interpretation}</p>
                                        </div>
                                    )
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    // Composant pour les actions de partage et sauvegarde du thème
    const ShareActions = ({ themeGuid }) => {
        const [copied, setCopied] = useState(false);
        const [bookmarkMsg, setBookmarkMsg] = useState('');

        if (!themeGuid) return null;

        const shareUrl = `${window.location.origin}/calcul-theme-astral/${themeGuid}`;
        const shareText = "Découvrez mon thème astral sur AstraLight";
        const encodedUrl = encodeURIComponent(shareUrl);
        const encodedText = encodeURIComponent(`${shareText} ${shareUrl}`);

        const handleCopyLink = async () => {
            try {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                try {
                    const textarea = document.createElement('textarea');
                    textarea.value = shareUrl;
                    textarea.style.position = 'fixed';
                    textarea.style.left = '-9999px';
                    document.body.appendChild(textarea);
                    textarea.focus();
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                } catch (e) {
                    console.error('Impossible de copier le lien', e);
                }
            }
        };

        const handleWebShare = async () => {
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: 'Mon thème astral',
                        text: shareText,
                        url: shareUrl
                    });
                } catch (e) {
                    // L'utilisateur peut annuler le partage; on ignore
                }
            } else {
                // Fallback: on copie le lien
                handleCopyLink();
            }
        };

        const addToFavorites = () => {
            const title = document.title || 'AstraLight - Thème Astral';
            try {
                // Tentatives pour anciens navigateurs
                if (window.external && 'AddFavorite' in window.external) {
                    window.external.AddFavorite(shareUrl, title);
                    return;
                }
                if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(title, shareUrl, '');
                    return;
                }
            } catch (e) {
                // On tombera sur les instructions ci-dessous
            }

            const isMac = navigator.platform && navigator.platform.toUpperCase().includes('MAC');
            const shortcut = isMac ? 'Cmd + D' : 'Ctrl + D';
            setBookmarkMsg(`Astuce: appuyez sur ${shortcut} pour ajouter cette page aux favoris`);
            setTimeout(() => setBookmarkMsg(''), 4000);
        };

        return (
            <div className="panel share-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', textAlign: 'center' }}>
                <h3 style={{ margin: 0 }}>Partager ce thème</h3>
                <div className="share-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
                    <button onClick={handleWebShare} className="icon-button" aria-label="Partager">
                        <i className="fa-solid fa-share-nodes" aria-hidden="true"></i>
                    </button>
                    <button onClick={handleCopyLink} className="icon-button" aria-label="Copier le lien">
                        <i className="fa-regular fa-copy" aria-hidden="true"></i>
                    </button>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur Facebook">
                        <i className="fa-brands fa-facebook-f" aria-hidden="true"></i>
                    </a>
                    <a href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent('Mon thème astral ✨')}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur X / Twitter">
                        <i className="fa-brands fa-x-twitter" aria-hidden="true"></i>
                    </a>
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur LinkedIn">
                        <i className="fa-brands fa-linkedin-in" aria-hidden="true"></i>
                    </a>
                    <a href={`https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent('Mon thème astral ✨')}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur Telegram">
                        <i className="fa-brands fa-telegram" aria-hidden="true"></i>
                    </a>
                    <a href={`https://wa.me/?text=${encodedText}`} target="_blank" rel="noopener noreferrer" className="icon-button" aria-label="Partager sur WhatsApp">
                        <i className="fa-brands fa-whatsapp" aria-hidden="true"></i>
                    </a>
                    <a href={`mailto:?subject=${encodeURIComponent('Mon thème astral')}&body=${encodedText}`} className="icon-button" aria-label="Partager par email">
                        <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                    </a>
                    <button onClick={addToFavorites} className="icon-button" aria-label="Ajouter aux favoris">
                        <i className="fa-regular fa-bookmark" aria-hidden="true"></i>
                    </button>
                </div>
                {copied && <small style={{ opacity: 0.8 }}>Lien copié ✓</small>}
                {bookmarkMsg && <small style={{ opacity: 0.8 }}>{bookmarkMsg}</small>}
            </div>
        );
    };

    const DisplayPlacements = ({ placements, isTrial, themeGuid }) => {
        const [emailSent, setEmailSent] = useState(false);
        const [emailError, setEmailError] = useState(null);
        const [isLoading, setIsLoading] = useState(false);

        const handleEmailSubmit = async (e) => {
            e.preventDefault();
            setIsLoading(true);
            setEmailError(null);

            try {
                const firstName = e.target.elements.firstName.value;
                const email = e.target.elements.email.value;
                const response = await api.post('/send_theme_link', {
                    firstName,
                    email,
                    guid: themeGuid
                });

                if (response.data.success) {
                    setEmailSent(true);
                } else {
                    setEmailError("Une erreur est survenue lors de l'envoi de l'email");
                }
            } catch (err) {
                setEmailError(err.response?.data?.error || "Une erreur est survenue");
            } finally {
                setIsLoading(false);
            }
        };

        // Fonction pour générer un lien vers l'article sur la planète si disponible
        const getPlanetLink = (astreName) => {
            const planetMap = {
                'Soleil': '/blog/planetes/soleil',
                'Lune': '/blog/planetes/lune',
                'Mercure': '/blog/planetes/mercure',
                'Vénus': '/blog/planetes/venus',
                'Mars': '/blog/planetes/mars',
                'Jupiter': '/blog/planetes/jupiter',
                'Saturne': '/blog/planetes/saturne',
                'Uranus': '/blog/planetes/uranus',
                'Neptune': '/blog/planetes/neptune',
                'Pluton': '/blog/planetes/pluton',
            };
            
            if (planetMap[astreName]) {
                return <Link to={planetMap[astreName]}>{astreName}<span className={`astro-symbol ${astreName.toLowerCase()}-symbol`}>{textReplacements[1].symbol[astreName]}</span></Link>;
            }
            
            return <>{astreName}<span className={`astro-symbol ${astreName.toLowerCase()}-symbol`}>{textReplacements[1].symbol[astreName]}</span></>;
        };

        const displayedPlacements = isTrial ? placements.slice(0, 3) : placements;

        return (
            <div className="section">
                <div className="section-header">
                    <h2>Placements Planétaires</h2>
                    <p>Les placements planétaires désignent la position des planètes dans les signes astrologiques et les maisons au moment de la naissance. Chaque planète représente une facette de la personnalité (ex : Soleil pour l'identité, Lune pour les émotions), le signe colore son expression, et la maison indique le domaine de vie concerné. L'interprétation de ces placements révèle les forces, défis et dynamiques influençant le chemin de vie.</p>
                </div>
                <div className="panel">
                    {displayedPlacements.filter(placement => placement && typeof placement === 'object').map((placement, index, array) => (
                        <div key={index} className={`placement-card ${isTrial && index === 2 ? 'fade-out' : ''}`}>
                            <div className="placement-header">
                                <h3>
                                    <span className={`planet-text`}>{getPlanetLink(placement.astre)}</span>
                                    {' sur '}
                                    <span className="sign-text">{placement.signe}<span className={`astro-symbol ${placement.signe.toLowerCase()}-symbol`}>{textReplacements[0].symbol[placement.signe]}</span></span>
                                    {' en '}
                                    <span className={`maison${placement.maison}-text`}>Maison {placement.maison}</span>
                                </h3>
                                {placement.degre_relatif && (
                                    <div className="placement-degrees">
                                        <span className="degree-absolute">{placement.longitude}° (absolus)</span>
                                        <span className="degree-relative">{placement.degre_relatif}° {placement.signe} (relatifs) - Tiers {placement.tier_degre}</span>
                                    </div>
                                )}
                            </div>

                            <div className="interpretation">
                                <p>{placement.interpretation_courte}</p>

                                {placement.interpretation_tier && (
                                    <div className="tier-interpretation">
                                        <h4>Influence du degré {placement.degre_relatif}° (Tiers {placement.tier_degre})</h4>
                                        <p>{placement.interpretation_tier}</p>
                                    </div>
                                )}
                                
                                {placement.forces && <p><strong>Forces:</strong> <span dangerouslySetInnerHTML={{ __html: placement.forces }} /></p>}
                                {placement.conseils && <p><strong>Conseils:</strong> <span dangerouslySetInnerHTML={{ __html: placement.conseils }} /></p>}
                            </div>

                            {index !== array.length - 1 && <hr />}
                        </div>
                    ))}
                    
                    {isTrial && (
                        <div className="email-capture-form panel">
                            {!emailSent ? (
                                <>
                                    <h3>Découvrez votre thème astral complet</h3>
                                    <p>Vous avez eu un aperçu de vos placements planétaires. Laissez-nous votre email pour recevoir une analyse personnalisée de votre thème astral, révélant les influences célestes qui façonnent votre vie.</p>
                                    <form className="trial-form" onSubmit={handleEmailSubmit}>
                                        <input 
                                            type="text"
                                            name="firstName"
                                            placeholder="Votre prénom" 
                                            required 
                                            disabled={isLoading}
                                        />
                                        <input 
                                            type="email" 
                                            name="email"
                                            placeholder="Votre email" 
                                            required 
                                            disabled={isLoading}
                                        />
                                        <button type="submit" disabled={isLoading}>
                                            {isLoading ? 'Envoi en cours...' : 'Recevoir mon thème complet'}
                                        </button>
                                        {emailError && <p className="error-message">{emailError}</p>}
                                    </form>
                                </>
                            ) : (
                                <div>
                                    <h3>✨ Votre email est en route !</h3>
                                    <p>Un message céleste vient de vous être envoyé, contenant le lien vers votre thème astral complet. Jetez un œil à votre boîte de réception (et n'oubliez pas de vérifier vos spams, au cas où).</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const DisplayBasicInfo = ({ rawData }) => {
        // Fonction pour générer un lien vers l'article sur le signe zodiacal si disponible
        const getSignLink = (signName) => {
            const signMap = {
                'Bélier': '/blog/signes/belier',
                'Taureau': '/blog/signes/taureau',
                'Gémeaux': '/blog/signes/gemeaux',
                'Cancer': '/blog/signes/cancer',
                'Lion': '/blog/signes/lion',
                'Vierge': '/blog/signes/vierge',
                'Balance': '/blog/signes/balance',
                'Scorpion': '/blog/signes/scorpion',
                'Sagittaire': '/blog/signes/sagittaire',
                'Capricorne': '/blog/signes/capricorne',
                'Verseau': '/blog/signes/verseau',
                'Poissons': '/blog/signes/poissons'
            };
            
            const signNom = rawData.informations_naissance.signe_base.nom;
            
            if (signMap[signNom]) {
                return (
                    <Link to={signMap[signNom]} className="sign-text">
                        {signNom}
                        <span className={`astro-symbol ${signNom.toLowerCase()}-symbol`}>
                            {textReplacements[0].symbol[signNom]}
                        </span>
                    </Link>
                );
            }
            
            return (
                <span className="sign-text">
                    {signNom}
                    <span className={`astro-symbol ${signNom.toLowerCase()}-symbol`}>
                        {textReplacements[0].symbol[signNom]}
                    </span>
                </span>
            );
        };

        return (
            <div className="basic-info-section">
                <div className="panel">
                    <p><strong>Date:</strong> {rawData.informations_naissance.date}</p>
                    <p><strong>Heure:</strong> {rawData.informations_naissance.heure}</p>
                    <p><strong>Ville:</strong> {rawData.informations_naissance.ville}</p>
                    <p><strong>Coordonnées:</strong> {rawData.informations_naissance.coordonnees.latitude}, {rawData.informations_naissance.coordonnees.longitude}</p>
                    <p><strong>Fuseau horaire:</strong> {rawData.informations_naissance.fuseau_horaire}</p>
                </div>

                <div className="panel">
                    <p><strong>Signe:</strong> {getSignLink(rawData.informations_naissance.signe_base.nom)}</p>
                    <p><strong>Élément:</strong> {rawData.informations_naissance.signe_base.element}</p>
                    <p><strong>Mode:</strong> {rawData.informations_naissance.signe_base.mode}</p>
                    <p><strong>Description:</strong> {rawData.informations_naissance.signe_base.description}</p>
                </div>
            </div>
        );
    };

    const DisplayRawData = ({ rawData }) => (
        <div className="section">
            <div className="section-header">
                <h2>Données Brutes</h2>
                <hr />
                <p>Les données brutes du thème astral comprennent les positions exactes des planètes, les angles des maisons et les aspects majeurs. Ces informations constituent la base de l'interprétation astrologique.</p>
            </div>
            <div className="raw-data-section">
                <div className="panel">
                    <h3>Positions Planétaires</h3>
                    <div className="scrollable-content">
                        {rawData.positions_astres.map((astre, index) => (
                            <div key={index} className="raw-data-item">
                                <span className="planet-text">
                                    {astre.astre}
                                    <span className={`astro-symbol ${astre.astre.toLowerCase()}-symbol`}>
                                        {textReplacements[1].symbol[astre.astre]}
                                    </span>
                                </span>
                                {' → '}
                                <span className="sign-text">
                                    {astre.signe}
                                    <span className={`astro-symbol ${astre.signe.toLowerCase()}-symbol`}>
                                        {textReplacements[0].symbol[astre.signe]}
                                    </span>
                                </span>
                                {` (${astre.longitude}° absolus, ${astre.degre_relatif}° ${astre.signe}) `}
                                <span className={`maison${astre.maison}-text`}>
                                    Maison {astre.maison}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    <div className="panel">
                        <h3>Angles Majeurs</h3>
                        {Object.entries(rawData.angles_majeurs).map(([angle, data], index) => (
                            <div key={index} className="raw-data-item">
                                <strong>{angle.replace(/_/g, ' ')}:</strong> {data.degre}° absolus, {data.degre_relatif}° en{' '}
                                <span className="sign-text">
                                    {data.signe}
                                    <span className={`astro-symbol ${data.signe.toLowerCase()}-symbol`}>
                                        {textReplacements[0].symbol[data.signe]}
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="panel">
                        <h3>Cuspides des Maisons</h3>
                        {rawData.maisons.map((maison, index) => (
                            <div key={index} className="raw-data-item">
                                <span className={`maison${maison.numero}-text`}>
                                    Maison {maison.numero}
                                </span>
                                : {maison.cuspide}° absolus, {maison.degre_relatif}° en{' '}
                                <span className="sign-text">
                                    {maison.signe}
                                    <span className={`astro-symbol ${maison.signe.toLowerCase()}-symbol`}>
                                        {textReplacements[0].symbol[maison.signe]}
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    useEffect(() => {
        if (guid) {
            loadThemeByGuid(guid);
        }
    }, [guid]);

    const loadThemeByGuid = async (guid) => {
        try {
            setLoadingState('full');
            const response = await api.get(`/theme/${guid}`);
            if (response.data.success) {
                setFullData(response.data.data);
                // Si on accède via un GUID, on désactive le trial définitivement
                localStorage.setItem('astra_trial', 'false');
                setTrial(false);
            }
            setLoadingState('idle');
        } catch (err) {
            setError(err.response?.data?.error || "Une erreur est survenue");
            setLoadingState('idle');
        }
    };

    const renderContent = () => {
        switch (loadingState) {
            case 'raw':
                return <Loader />;

            case 'full':
                return (
                    <>
                        <FormatText replacements={textReplacements}>
                            <div className="theme-result">
                                {rawData && <DisplayBasicInfo rawData={rawData} />}
                                {rawData && <DisplayRawData rawData={rawData} />}
                            </div>
                        </FormatText>
                        <Loader />
                    </>
                );

            case 'idle':
                if (fullData) {
                    return (
                        <FormatText replacements={textReplacements}>
                            <div className="theme-result">
                                {fullData.raw_data && <DisplayBasicInfo rawData={fullData.raw_data} />}
                                <ShareActions themeGuid={fullData.guid} />
                                {fullData.raw_data && <DisplayRawData rawData={fullData.raw_data} />}
                                <DisplayPlacements 
                                    placements={fullData.interpretations.placements} 
                                    isTrial={trial} 
                                    themeGuid={fullData.guid}
                                />
                                {!trial && (
                                    <DisplayAspects aspects={fullData.interpretations.aspects} />
                                )}
                            </div>
                        </FormatText>
                    );
                }
                return null;

            default:
                return null;
        }
    };

    return (
        <>
            <Helmet>
                <title>Calcul Thème Astral Gratuit | Carte du Ciel Personnalisée</title>
                <meta name="description" content="Calculez gratuitement votre thème astral en ligne avec date, heure et lieu de naissance précis. Positions planétaires exactes et interprétation personnalisée." />
                
                {/* Balises canoniques */}
                <link rel="canonical" href="https://astralight.xyz/calcul-theme-astral" />
                
                {/* Balises Open Graph pour Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://astralight.xyz/calcul-theme-astral" />
                <meta property="og:title" content="Calcul Thème Astral Gratuit | Carte du Ciel Personnalisée" />
                <meta property="og:description" content="Découvrez votre carte du ciel et thème natal avec notre calculateur astrologique précis. Positions planétaires complètes et interprétation personnalisée." />
                <meta property="og:image" content="https://astralight.xyz/images/astralight_og.webp" />
                <meta property="og:site_name" content="AstraLight" />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Calcul Thème Astral Gratuit | Carte du Ciel Personnalisée" />
                <meta name="twitter:description" content="Calculez votre thème natal complet: ascendant, maisons astrologiques, positions planétaires et aspects." />
                <meta name="twitter:image" content="https://astralight.xyz/images/astralight_og.webp" />
                
                {/* Autres balises méta importantes */}
                <meta name="robots" content="index, follow" />
                <meta name="keywords" content="thème astral, calcul thème astral gratuit, carte du ciel, thème natal, positions planètes, calcul ascendant, interprétation thème" />
                <meta name="author" content="AstraLight" />
                
                {/* Schemas structurés */}
                <script type="application/ld+json">
                    {JSON.stringify(getToolSchema())}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(getHowToSchema())}
                </script>
            </Helmet>
            
            <Navbar />

            <div className="container theme-container">
                {!guid ? (
                    <>
                        <PageHeader
                            eyebrow="Votre empreinte céleste"
                            title="Thème astral & carte du ciel"
                            subtitle="Une analyse astrologique précise et détaillée, basée sur la position exacte des astres au moment de votre naissance. Votre thème astral est unique, comme une empreinte digitale stellaire qui raconte votre histoire personnelle."
                        >
                            <span className="page-header-chip">
                                <i className="fa-solid fa-sun" aria-hidden="true"></i>
                                Gratuit, sans inscription
                            </span>
                        </PageHeader>

                        <div className="header-form panel astra-form">
                            <h2 className="form-title">Entrez vos informations de naissance</h2>
                            <hr className="form-title-underline" />
                            <form onSubmit={handleSubmit}>
                                <div className="astra-form-fields">
                                    <div className="form-group">
                                        <label style={{ marginTop: 0 }}>Date de naissance:</label>
                                        <input
                                            type="date"
                                            name="birth_date"
                                            value={formData.birth_date}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label style={{ marginTop: 0 }}>Heure de naissance:</label>
                                        <input
                                            type="time"
                                            name="birth_time"
                                            value={formData.birth_time}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label style={{ marginTop: 0 }}>Ville de naissance:</label>
                                        <div className="city-search-container">
                                            <input
                                                className='city-input'
                                                type="text"
                                                value={citySearch}
                                                onChange={handleCitySearch}
                                                placeholder="Commencez à taper le nom d'une ville..."
                                                required
                                            />
                                            {showSuggestions && suggestions.length > 0 && (
                                                <ul className="city-suggestions">
                                                    {suggestions.map((suggestion, index) => (
                                                        <li
                                                            key={index}
                                                            onClick={() => handleCitySelect(suggestion)}
                                                        >
                                                            {suggestion.display}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button type="submit" className="btn-gold astra-submit" disabled={loadingState !== 'idle'}>
                                    <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
                                    {loadingState === 'raw' ? 'Calcul en cours...' : 'Calculer mon thème'}
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <PageHeader
                        eyebrow="Votre empreinte céleste"
                        title="Découvrez un autre thème astral"
                        subtitle="Explorez un nouveau thème astral pour découvrir les mystères de l'univers et les influences célestes qui vous entourent."
                    >
                        <button className="btn-gold" onClick={() => window.location.href = '/calcul-theme-astral'}>
                            <i className="fa-solid fa-sun" aria-hidden="true"></i>
                            Explorer un autre thème
                        </button>
                    </PageHeader>
                )}

                {error && <div className="error">{error}</div>}

                {renderContent()}

                {/* Sections SEO toujours visibles */}
                <hr />
                <ThemeAstralInfoContent />
                <InterpretationGuide />
                <FAQ />
            </div>

            <Footer />
        </>
    );
};

export default Astra;
